import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import {
  getPublicIdFromCloudinaryUrl,
  isCloudinaryUrl,
} from '../utils/cloudinaryUpload';
import { uploadToCdn, deleteFromCdn, isUnicornCdnUrl } from '../utils/cdnUpload';

export const usePropertyImages = (propertyId?: string) => {
  const [images, setImages] = useState<any[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [primaryImageId, setPrimaryImageId] = useState<string | null>(null);
  const [localImageUrls, setLocalImageUrls] = useState<{[key: string]: string}>({});
  const [error, setError] = useState<string | null>(null);
  const [dragInProgress, setDragInProgress] = useState(false);

  // Fetch existing images when propertyId changes
  useEffect(() => {
    if (propertyId) {
      fetchExistingImages();
    }
  }, [propertyId]);

  const fetchExistingImages = async () => {
    try {
      const { data: columnInfo } = await supabase
        .from('property_images')
        .select('*')
        .limit(1);

      const hasPositionColumn = columnInfo && columnInfo.length > 0 && 'position' in columnInfo[0];

      let query = supabase
        .from('property_images')
        .select('*')
        .eq('property_id', propertyId);

      if (hasPositionColumn) {
        query = query.order('position', { ascending: true });
      } else {
        query = query.order('created_at', { ascending: true });
      }

      const { data: existingImages, error } = await query;

      if (error) throw error;

      if (existingImages) {
        const imagesWithPosition = existingImages.map((img, index) => ({
          ...img,
          position: img.position !== undefined ? img.position : index
        }));

        setImages(imagesWithPosition);
        const primaryImage = imagesWithPosition.find(img => img.is_primary);
        if (primaryImage) {
          setPrimaryImageId(primaryImage.id);
        }
      }
    } catch (error) {
      console.error('Error fetching existing images:', error);
      setError('Error loading existing images');
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!propertyId) {
      setError('Property ID is required to upload images');
      return;
    }

    const maxPosition = images.reduce((max, img) => {
      const imgPosition = img.position || 0;
      return imgPosition > max ? imgPosition : max;
    }, -1);

    const newImages = acceptedFiles.map((file, index) => ({
      id: uuidv4(),
      file,
      isNew: true,
      position: maxPosition + 1 + index
    }));

    // Create local URLs for immediate preview
    const newLocalUrls = newImages.reduce((acc, img) => ({
      ...acc,
      [img.id]: URL.createObjectURL(img.file)
    }), {});

    setLocalImageUrls(prev => ({ ...prev, ...newLocalUrls }));
    setImages(prev => [...prev, ...newImages]);

    // Check position column once
    const { data: columnInfo } = await supabase
      .from('property_images')
      .select('*')
      .limit(1);
    const hasPositionColumn = columnInfo && columnInfo.length > 0 && 'position' in columnInfo[0];

    let localPrimarySet = !!primaryImageId;

    for (const image of newImages) {
      try {
        setUploadProgress(prev => ({ ...prev, [image.id]: 0 }));

        // Upload to Unicorn CDN (via Netlify proxy — API key never hits the browser).
        const result = await uploadToCdn(
          image.file,
          (percent) => setUploadProgress(prev => ({ ...prev, [image.id]: percent }))
        );

        const publicUrl = result.cdn_url;
        const unicornFileId = result.file?.id ?? null;

        const shouldBePrimary = !localPrimarySet;
        const insertData: any = {
          property_id: propertyId,
          url: publicUrl,
          is_primary: shouldBePrimary,
          unicorn_file_id: unicornFileId,
        };

        if (hasPositionColumn) {
          insertData.position = image.position;
        }

        const { data: imageRecord, error: dbError } = await supabase
          .from('property_images')
          .insert(insertData)
          .select()
          .single();

        if (dbError) throw dbError;

        if (shouldBePrimary) {
          localPrimarySet = true;
          setPrimaryImageId(imageRecord.id);
        }

        setUploadProgress(prev => ({ ...prev, [image.id]: 100 }));

        setTimeout(() => {
          setImages(prev => prev.map(img =>
            img.id === image.id
              ? { ...imageRecord, isNew: false, position: image.position }
              : img
          ));

          setTimeout(() => {
            setUploadProgress(prev => {
              const { [image.id]: removed, ...rest } = prev;
              return rest;
            });
          }, 1000);
        }, 500);

      } catch (err) {
        console.error('Error uploading image:', err);
        setImages(prev => prev.filter(img => img.id !== image.id));
        setUploadProgress(prev => {
          const { [image.id]: removed, ...rest } = prev;
          return rest;
        });
        setError('Errore nel caricamento dell\'immagine. Riprova.');
      }
    }
  }, [propertyId, primaryImageId, images]);

  const handleSetPrimary = async (id: string) => {
    if (!propertyId) return;

    try {
      await supabase
        .from('property_images')
        .update({ is_primary: false })
        .eq('property_id', propertyId);

      const { error } = await supabase
        .from('property_images')
        .update({ is_primary: true })
        .eq('id', id);

      if (error) throw error;

      setPrimaryImageId(id);

      setImages(prev => prev.map(img => ({
        ...img,
        is_primary: img.id === id
      })));
    } catch (error) {
      console.error('Error setting primary image:', error);
      setError('Error setting primary image');
    }
  };

  const handleRemoveImage = async (id: string) => {
    const image = images.find(img => img.id === id);
    if (!image) return;

    try {
      if (!image.isNew) {
        // Delete from database
        const { error: dbError } = await supabase
          .from('property_images')
          .delete()
          .eq('id', id);

        if (dbError) throw dbError;

        // Delete from storage. Route by origin: Unicorn (current), Cloudinary (legacy), Supabase (older).
        // All best-effort: the DB record is already gone; a CDN orphan is not user-visible.
        if (image.url) {
          if (isUnicornCdnUrl(image.url) && image.unicorn_file_id) {
            deleteFromCdn(image.unicorn_file_id)
              .catch(err => console.warn('Unicorn delete warning:', err));
          } else if (isCloudinaryUrl(image.url)) {
            const publicId = getPublicIdFromCloudinaryUrl(image.url);
            if (publicId) {
              fetch('/.netlify/functions/cloudinary-delete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ publicId }),
              }).catch(err => console.warn('Cloudinary delete warning:', err));
            }
          } else {
            const filePath = image.url.split('/').pop();
            if (filePath) {
              await supabase.storage
                .from('properties')
                .remove([`${propertyId}/${filePath}`])
                .catch(err => console.warn('Supabase storage delete warning:', err));
            }
          }
        }
      }

      setImages(prev => prev.filter(img => img.id !== id));

      setUploadProgress(prev => {
        const { [id]: removed, ...rest } = prev;
        return rest;
      });

      setLocalImageUrls(prev => {
        const { [id]: removed, ...rest } = prev;
        return rest;
      });

      if (primaryImageId === id) {
        const remainingImages = images.filter(img => img.id !== id);
        if (remainingImages.length > 0) {
          await handleSetPrimary(remainingImages[0].id);
        } else {
          setPrimaryImageId(null);
        }
      }

      const remainingImages = images.filter(img => img.id !== id);
      if (remainingImages.length > 0) {
        setImages(remainingImages.map((img, index) => ({ ...img, position: index })));
      }

    } catch (error) {
      console.error('Error removing image:', error);
      setError('Error removing image');
    }
  };

  const handleReorderImages = async (sourceIndex: number, targetIndex: number) => {
    if (sourceIndex === targetIndex) return;

    try {
      setDragInProgress(true);

      const newImages = [...images];
      const [movedImage] = newImages.splice(sourceIndex, 1);
      newImages.splice(targetIndex, 0, movedImage);

      const updatedImages = newImages.map((img, index) => ({ ...img, position: index }));
      setImages(updatedImages);

      try {
        await retryOperation(() => updateImagePositions(updatedImages), 3);
      } catch (updateError) {
        console.error('Failed to update image positions after retries:', updateError);
        setError('Errore nel riordinamento: le modifiche sono solo locali.');
      }
    } catch (error) {
      console.error('Error reordering images:', error);
      setError('Error reordering images');
      fetchExistingImages();
    } finally {
      setDragInProgress(false);
    }
  };

  const retryOperation = async (operation: () => Promise<void>, maxRetries: number) => {
    let lastError;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        await operation();
        return;
      } catch (error) {
        lastError = error;
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
      }
    }
    throw lastError;
  };

  const updateImagePositions = async (imagesToUpdate = images) => {
    if (!propertyId || imagesToUpdate.length === 0) return;

    const { data: columnInfo, error: columnError } = await supabase
      .from('property_images')
      .select('*')
      .limit(1);

    if (columnError) throw new Error(`Failed to check position column: ${columnError.message}`);

    const hasPositionColumn = columnInfo && columnInfo.length > 0 && 'position' in columnInfo[0];
    if (!hasPositionColumn) return;

    const updates = imagesToUpdate
      .filter(img => !img.isNew)
      .map((img, index) => ({ id: img.id, position: index }));

    for (const update of updates) {
      const { error } = await supabase
        .from('property_images')
        .update({ position: update.position })
        .eq('id', update.id);

      if (error) throw new Error(`Failed to update position for image ${update.id}: ${error.message}`);
    }
  };

  return {
    images,
    uploadProgress,
    primaryImageId,
    localImageUrls,
    error,
    dragInProgress,
    handleSetPrimary,
    handleRemoveImage,
    handleReorderImages,
    onDrop
  };
};
